/**
 * Created by razial on 02.05.2023
 */

/**
 * Per-node gesture recognizer. A single touch is offered to every handler in the
 * scene graph (topmost first); handlers race to "claim" it.
 *
 * Claim protocol:
 *   - A touch is unclaimed until some handler claims it (touch.handler, via claimTouch).
 *   - Once claimed, every other handler bails out via canCaptureTouch. This is what
 *     suppresses a click on a parent while a child drags, a grid-click during a pinch, etc.
 *   - Priority is therefore "first to claim wins". When each gesture claims:
 *       click       - latest: only at release, so a small in-place move can still tap.
 *       drag        - when movement passes the threshold.
 *       pinch       - claims both fingers when a second touch forms; may STEAL a finger
 *                     that is already mid-drag (isDragging), and on release hands the
 *                     surviving finger back to the drag handler (zoom -> pan).
 *       press/longpress/swallow - immediately on accept.
 *   - touch.gesture records which gesture claimed the touch, for inspection/logging only.
 *     Control flow reads authoritative per-handler state (e.g. isDragging), never this label.
 */
class PointerHandler {
    constructor(target, options) {
        options = options || {};

        this._creationStack = new Error().stack;

        this.touches = new Map();
        this.target = target;
        this.swallowEvents = options.swallowEvents;
        this.filter = options.filter || function () {
            return true;
        };

        this.onMouseZoom = options.onMouseZoom;
        this.onPinchZoom = options.onPinchZoom;
        this.onMouseScroll = options.onMouseScroll;

        this.usesPress = Boolean(options.onPressed || options.onLongPressStart);
        if (this.usesPress) {
            this.onPressed = options.onPressed;
            this.onLongPressStart = options.onLongPressStart;
            this.onLongPressEnd = options.onLongPressEnd;

            this.usesLongPress = Boolean(options.onLongPressStart);
        }

        this.onOuterTouch = options.onOuterTouch;

        this.usesClick = Boolean(options.onClick || options.onLongClick || options.onDoubleClick);
        if (this.usesClick) {
            this.onClick = options.onClick;
            if (cleverapps.config.debugMode) {
                this.target.clickPointerHandler = this;
            }

            this.onLongClick = options.onLongClick;
            this.onDoubleClick = options.onDoubleClick;

            this.longClickDuration = PointerHandler.LONG_CLICK_TIMEOUT;
            this.doubleClickDuration = PointerHandler.DOUBLE_CLICK_WAIT;
        }

        this.usesDrag = Boolean(options.followPointer || options.onDragMoveNotThrottled || options.onDragStart || options.onDragMove || options.onDragEnd);
        if (this.usesDrag) {
            this.dragDirection = options.dragDirection;
            this.dragControls = options.dragControls;
            this.onDragStart = options.onDragStart || function () {
                return true;
            };
            this._dragMoveCallback = options.onDragMove || (() => {});
            this.onDragMove = cleverapps.throttle(options.throttle || PointerHandler.DRAG_THROTTLE, this._dragMoveCallback);
            this.onDragEnd = options.onDragEnd || function () {};

            if (options.onDragMoveNotThrottled) {
                this.onDragMoveNotThrottled = options.onDragMoveNotThrottled;
            }

            this.followPointer = options.followPointer;
            this.instantDrag = options.instantDrag;
            this.dragHoldTime = options.holdForDrag ? PointerHandler.DRAG_HOLD_TIME : 0;
        }

        if (this.usesPress || this.usesClick || this.usesDrag || this.onPinchZoom || this.swallowEvents) {
            this.onStatePressed = options.onStatePressed || function () {};
            this.onStateReleased = options.onStateReleased || function () {};
        }

        this.isHovered = false;
        if (options.hover) {
            options.onMouseOver = options.onMouseOver || function () {};
            options.onMouseOut = options.onMouseOut || function () {};
            options.onMouseMove = options.onMouseMove || function () {};
        }
        this.useHover = Boolean(options.onMouseOver || options.onMouseOut || options.onMouseMove);
        if (this.useHover) {
            this.onMouseOver = options.onMouseOver;
            this.onMouseOut = options.onMouseOut;
            this.onMouseMove = options.onMouseMove;
        }

        this.interactiveScale = !this.onPinchZoom && !this.usesDrag && !this.usesPress && (options.interactiveScale !== false) && (this.usesClick || this.useHover);

        this.needsToHandleMouseScroll = this.onMouseZoom || this.onMouseScroll;
        this.needsToHandleMouseMove = this.useHover || this.interactiveScale;
        this.needsToHandleTouches = this.usesPress || this.usesClick || this.usesDrag || this.onPinchZoom || this.onOuterTouch;

        // Events are dispatched by InputDispatcher, no need to register listeners here

        this.isLabelTTF = target instanceof cc.LabelTTF;
    }

    static setState(handler, state) {
        if (!PointerHandler.TargetStates.has(handler.target)) {
            PointerHandler.TargetStates.set(handler.target, {
                pressed: new Set(),
                hovered: new Set()
            });
        }

        PointerHandler.TargetStates.get(handler.target)[state].add(handler);
    }

    static removeState(handler, state) {
        const data = PointerHandler.TargetStates.get(handler.target);
        if (data) {
            if (data[state].delete(handler)) {
                if (data.pressed.size === 0 && data.hovered.size === 0) {
                    PointerHandler.TargetStates.delete(handler.target);
                }

                return true;
            }
        }

        return false;
    }

    static getState(target, state) {
        const data = PointerHandler.TargetStates.get(target);
        if (data) {
            return data[state].size > 0;
        }

        return false;
    }

    remove() {
        cleverapps.pointerManager.remove(this);
    }

    destructor() {
        this.destructed = true;

        this._release();

        this.touches.forEach((data) => {
            const { touch } = data;

            // Release any touch this handler owns, so destroying it mid-gesture can't strand
            // the touch (owned by a dead handler nobody can reclaim it from) or leave a
            // partner pointing at a dead pinch. Guarded by ownership: a handler that merely
            // tracks a touch owned elsewhere must not disturb the real owner.
            if (touch.handler === this) {
                if (touch.pinchTouch) {
                    this.unlinkPinch(touch, touch.pinchTouch);
                }

                touch.handler = undefined;
                touch.gesture = PointerHandler.GESTURE_NONE;
            }

            if (data.longPressTimeout) {
                cleverapps.timeouts.clearTimeout(data.longPressTimeout);
            }

            if (data.changeFocusListener) {
                data.changeFocusListener.clear();
                data.changeFocusListener = undefined;
            }

            if (data.freeFocus) {
                data.freeFocus();
                data.freeFocus = undefined;
            }
        });
    }

    _release() {
        if (PointerHandler.removeState(this, "pressed") && this.onStateReleased) {
            this.onStateReleased();
        }
        if (PointerHandler.removeState(this, "hovered") && this.onMouseOut) {
            this.onMouseOut();
        }

        this._updateInteractiveScale();
    }

    _updateInteractiveScale() {
        if (!this.interactiveScale) {
            return;
        }

        const maxScale = MaxInteractiveScale(this.target);
        const baseScale = BaseScale(this.target);

        if (PointerHandler.getState(this.target, "pressed")) {
            this.target.setScale(baseScale.x / maxScale, baseScale.y / maxScale);
        } else if (PointerHandler.getState(this.target, "hovered")) {
            this.target.setScale(baseScale.x * maxScale, baseScale.y * maxScale);
        } else {
            this.target.setScale(baseScale.x, baseScale.y);
        }
    }

    setEnabled(enabled) {
        this._enabled = enabled;

        if (!enabled) {
            this._release();
        }
    }

    isEnabled() {
        return this._enabled !== false;
    }

    hitTestTouch(touch) {
        let point = this.target.convertToNodeSpace(touch.getLocation());
        let rect;
        if (this.target.clickRect) {
            rect = this.target.clickRect;
        } else {
            rect = cc.rect(0, 0, this.target.width, this.target.height);
        }
        if (this.isLabelTTF) {
            rect.x *= cleverapps.resolution.devicePixelRatio;
            rect.y *= cleverapps.resolution.devicePixelRatio;
            rect.width *= cleverapps.resolution.devicePixelRatio;
            rect.height *= cleverapps.resolution.devicePixelRatio;
        }

        if (!cc.rectContainsPoint(rect, point)) {
            return false;
        }

        if (!this.target.isDisplayed()) {
            return false;
        }

        if (!cleverapps.focusManager.checkEventNode(this.target)) {
            return false;
        }

        for (let node = this.target; node; node = node.parent) {
            const cropContainer = node.getCropContainer();
            if (cropContainer) {
                if (!cc.rectContainsPoint(cropContainer, point)) {
                    return false;
                }
            }
            point = cc.pointApplyAffineTransform(point, node.getNodeToParentTransform());
        }

        return true;
    }

    // Drag-start gate: a directional scrollview only counts motion along its scroll
    // axis, so an off-axis smear doesn't start a scroll. NOT for click suppression —
    // for that use movedBeyondClickThreshold (a tap must stay put in BOTH axes).
    isInClickThreshold(touch) {
        const threshold = cleverapps.styles.PointerHandler.clickThreshold;
        const from = touch.getStartLocation();
        const to = touch.getLocation();

        if (this.dragDirection) {
            if (this.target.direction === cleverapps.UI.ScrollView.DIR_VERTICAL) {
                return Math.abs(from.y - to.y) < threshold;
            } if (this.target.direction === cleverapps.UI.ScrollView.DIR_HORIZONTAL) {
                return Math.abs(from.x - to.x) < threshold;
            }
        }

        return cc.pDistanceSQ(from, to) < threshold * threshold;
    }

    movedBeyondClickThreshold(touch) {
        const threshold = cleverapps.styles.PointerHandler.clickThreshold;
        return cc.pDistanceSQ(touch.getStartLocation(), touch.getLocation()) >= threshold * threshold;
    }

    bubblesFromInnerNode(touch) {
        if (touch.nodes && !touch.nodes.has(this.target)) {
            return false;
        }

        if (!touch.nodes) {
            touch.nodes = new Set();
            touch.innermost = this;

            for (let node = this.target; node; node = node.parent) {
                touch.nodes.add(node);
            }
        }

        return true;
    }

    // Claim a touch for a specific gesture: take ownership (touch.handler) and tag it
    // with the gesture for inspection. The tag is a label only — never read for control
    // flow, which uses authoritative per-handler state (see isDragging).
    claimTouch(touch, gesture) {
        touch.gesture = gesture;
        touch.handler = this;
    }

    canCaptureTouch(touch) {
        return !touch.handler || touch.handler === this;
    }

    // Authoritative: is this touch right now being dragged by THIS handler? Lets the
    // pinch path ask the drag handler directly instead of reaching into its touches map.
    isDragging(touch) {
        const data = this.touches.get(touch);
        return Boolean(data) && data.state === PointerHandler.STATE_DRAG_MOVE && touch.handler === this;
    }

    findDragHandler() {
        return cleverapps.pointerManager.listHandlersForTarget(this.target).find((h) => h.usesDrag);
    }

    // Form a pinch between two fingers: link them and claim both for this handler.
    linkPinch(a, b) {
        a.pinchTouch = b;
        a.pinchPrevPosition = a.getLocation();
        b.pinchTouch = a;
        b.pinchPrevPosition = b.getLocation();

        this.claimTouch(a, PointerHandler.GESTURE_PINCH);
        this.claimTouch(b, PointerHandler.GESTURE_PINCH);
    }

    unlinkPinch(a, b) {
        a.pinchTouch = undefined;
        a.pinchPrevPosition = undefined;
        b.pinchTouch = undefined;
        b.pinchPrevPosition = undefined;
    }

    needProcessTouch(touch) {
        if (!this.canCaptureTouch(touch)) {
            return false;
        }

        if (!this.filter(touch)) {
            return false;
        }

        if (this.touches.size > 0 && !this.onPinchZoom) {
            return false;
        }

        if (this.touches.size > 1 && this.onPinchZoom) {
            return false;
        }

        return true;
    }

    handleTouchBegan(touch, event) {
        if (PointerHandler.blocked && !this.target.passBlocked) {
            return false;
        }
        if (!this.needsToHandleTouches) {
            return false;
        }

        if (!this.isEnabled()) {
            return false;
        }

        if (!this.hitTestTouch(touch)) {
            if (this.onOuterTouch && this.onOuterTouch(touch, event) && this.swallowEvents) {
                this.claimTouch(touch, PointerHandler.GESTURE_SWALLOW);
            }
            return false;
        }

        if (!this.needProcessTouch(touch)) {
            return false;
        }

        if (!this.bubblesFromInnerNode(touch)) {
            return false;
        }

        if (this.swallowEvents) {
            this.claimTouch(touch, PointerHandler.GESTURE_SWALLOW);
        }

        const touchData = {
            touch,
            startTime: cleverapps.timeouts.getTime(),
            startTouch: this.followPointer ? this.target.parent.convertToNodeSpace(touch.getLocation()) : undefined,
            startPos: this.followPointer ? this.target.getPosition() : undefined
        };

        let state = PointerHandler.STATE_NONE;
        if (this.usesDrag) {
            state = PointerHandler.STATE_DRAG_START;
        } else if (this.usesClick) {
            state = PointerHandler.STATE_CLICK;
        }

        if (this.onLongPressStart) {
            touchData.longPressTimeout = cleverapps.timeouts.setTimeout(() => {
                if (this.canCaptureTouch(touch) && this.onLongPressStart(touch)) {
                    touchData.state = PointerHandler.STATE_LONG_PRESS;

                    this.claimTouch(touch, PointerHandler.GESTURE_LONG_PRESS);
                }
            }, PointerHandler.LONG_CLICK_TIMEOUT);
        }

        if (this.onPressed && this.onPressed(touch, event)) {
            state = PointerHandler.STATE_NONE;
            this.claimTouch(touch, PointerHandler.GESTURE_PRESS);
        } else if (this.onPinchZoom) {
            let secondTouch;
            const dragHandler = this.findDragHandler();
            for (const data of this.touches.values()) {
                // A finger is eligible as the pinch partner if it is free, or if it is
                // currently mid-drag on this target (we then steal it from the drag).
                const switchingFromDrag = Boolean(dragHandler) && dragHandler.isDragging(data.touch);
                if ((this.canCaptureTouch(data.touch) || switchingFromDrag) && !data.touch.pinchTouch) {
                    secondTouch = data;
                    if (switchingFromDrag) {
                        dragHandler.handleTouchEnded(data.touch);
                    }
                    break;
                }
            }
            if (secondTouch) {
                secondTouch = secondTouch.touch;

                state = PointerHandler.STATE_NONE;

                this.linkPinch(touch, secondTouch);
            }
        }

        touchData.state = state;

        this.touches.set(touch, touchData);

        if (touch.innermost && touch.innermost.target === this.target) {
            PointerHandler.setState(this, "pressed");
            if (this.onStatePressed) {
                this.onStatePressed(touch);
            }

            this._updateInteractiveScale();
        }

        if (this.usesClick || this.onPinchZoom || this.usesLongPress) {
            touch.useThreshold = true;
        }

        if (this.instantDrag) {
            this.handleTouchMoved(touch);
        }

        return true;
    }

    handleTouchMoved(touch, event) {
        const data = this.touches.get(touch);

        if (!cleverapps.focusManager.checkEventNode(this.target)) {
            return;
        }

        if (!data || !this.canCaptureTouch(touch)) {
            return;
        }

        if (touch.pinchTouch) {
            const secondTouch = touch.pinchTouch;

            const prevDistance = this.calcPinchDistance(touch.pinchPrevPosition, secondTouch.pinchPrevPosition);
            const currentDistance = this.calcPinchDistance(touch.getLocation(), secondTouch.getLocation());
            const diffDistance = currentDistance - prevDistance;

            if (Math.abs(diffDistance) > 3) {
                touch.pinchPrevPosition = touch.getLocation();
                secondTouch.pinchPrevPosition = secondTouch.getLocation();
                this.onPinchZoom(diffDistance * 0.00085);
            }
            return;
        }

        if (data.state === PointerHandler.STATE_DRAG_START) {
            if (data.startTime + this.dragHoldTime > cleverapps.timeouts.getTime()) {
                return;
            }

            if (touch.useThreshold && !this.instantDrag && this.isInClickThreshold(touch)) {
                return;
            }

            if (this.handleDragStart(touch, event, data)) {
                data.state = PointerHandler.STATE_DRAG_MOVE;
                if (data.longPressTimeout) {
                    cleverapps.timeouts.clearTimeout(data.longPressTimeout);
                }
                this.claimTouch(touch, PointerHandler.GESTURE_DRAG);
            } else {
                data.state = PointerHandler.STATE_CLICK;
            }
        }

        if (data.state === PointerHandler.STATE_DRAG_MOVE) {
            let forceDragEnd = false;
            if (this.onDragMoveNotThrottled) {
                forceDragEnd = this.onDragMoveNotThrottled(touch, event, data);
            }

            if (this.followPointer) {
                const now = this.target.parent.convertTouchToNodeSpace(touch);

                const dx = now.x - data.startTouch.x;
                const dy = now.y - data.startTouch.y;

                this.target.setPosition(
                    data.startPos.x + dx,
                    data.startPos.y + dy
                );
            }

            if (forceDragEnd) {
                this.handleDragEnd(touch, event, data);
                data.state = PointerHandler.STATE_NONE;
            } else {
                this.handleDragMove(touch, event);
            }
        }
    }

    handleTouchEnded(touch, event) {
        const data = this.touches.get(touch);
        this.touches.delete(touch);

        if (touch.innermost && touch.innermost.target === this.target) {
            PointerHandler.removeState(this, "pressed");
            if (this.onStateReleased) {
                this.onStateReleased(touch);
            }

            this._updateInteractiveScale();
        }

        if (!data) {
            return;
        }

        if (data.longPressTimeout) {
            cleverapps.timeouts.clearTimeout(data.longPressTimeout);
        }

        if (!cleverapps.focusManager.checkEventNode(this.target)) {
            return;
        }

        if (!this.canCaptureTouch(touch)) {
            return;
        }

        if (data.state === PointerHandler.STATE_DRAG_START) {
            data.state = PointerHandler.STATE_CLICK;
        }

        if (touch.pinchTouch) {
            const secondTouch = touch.pinchTouch;

            this.unlinkPinch(touch, secondTouch);

            const dragHandler = this.findDragHandler();
            if (dragHandler) {
                dragHandler.claimTouch(secondTouch, PointerHandler.GESTURE_DRAG);

                dragHandler.handleTouchBegan(secondTouch);
            }
        }

        if (data.state === PointerHandler.STATE_DRAG_MOVE) {
            this._dragMoveCallback(touch, event);
            this.handleDragEnd(touch, event, data);

            if (this.instantDrag && !this.movedBeyondClickThreshold(touch)) {
                data.state = PointerHandler.STATE_CLICK;
            } else {
                data.state = PointerHandler.STATE_NONE;
            }
        }

        if (data.state === PointerHandler.STATE_LONG_PRESS && this.onLongPressEnd) {
            this.onLongPressEnd(touch);
        }

        if (data.state === PointerHandler.STATE_CLICK && !this.usesClick) {
            data.state = PointerHandler.STATE_NONE;
        }

        if (data.state === PointerHandler.STATE_CLICK) {
            data.state = PointerHandler.STATE_NONE;

            if (!this.hitTestTouch(touch)) {
                return;
            }

            // A tap is a tap only if the finger stayed put. Without this, onClick fires
            // no matter how far the finger moved unless some drag handler happened to
            // capture the touch first — the root cause of click-after-drag/pinch bugs.
            if (this.movedBeyondClickThreshold(touch)) {
                return;
            }

            if (this.onLongClick && data.startTime + this.longClickDuration <= cleverapps.timeouts.getTime()) {
                this.claimTouch(touch, PointerHandler.GESTURE_CLICK);

                this.onLongClick(touch, event);
                return;
            }

            if (this.onDoubleClick) {
                if (this.lastClick) {
                    const lastClick = this.lastClick;
                    this.lastClick = undefined;

                    clearTimeout(lastClick.timeout);

                    if (!this.movedBeyondClickThreshold(lastClick.touch)) {
                        this.claimTouch(touch, PointerHandler.GESTURE_CLICK);

                        this.onDoubleClick(touch, event);
                        return;
                    }
                }

                const lastClickTimeout = setTimeout(() => {
                    clearTimeout(this.lastClick.timeout);
                    this.lastClick = undefined;
                    this.handleClick(touch, event);
                }, this.doubleClickDuration);

                this.lastClick = {
                    touch,
                    timeout: lastClickTimeout
                };

                return;
            }

            this.claimTouch(touch, PointerHandler.GESTURE_CLICK);

            this.handleClick(touch, event);
        }
    }

    handleTouchCancelled(touch, event) {
        return this.handleTouchEnded(touch, event);
    }

    handleClick(touch, event) {
        if (this.onClick) {
            this.onClick(touch, event);
        }
    }

    handleDragStart(touch, event, data) {
        if (this.onDragStart(touch, event) && !this.destructed) {
            if (this.followPointer) {
                this._originalTargetParent = this.target.parent;
                const movingNode = cleverapps.Scenes.GetRunningScene().movingNode;
                this._dragNode = new cc.Node();
                movingNode.addChild(this._dragNode);
                const scale = cc.Base.AdjustScaleTo(this.target, movingNode);
                const rotation = cc.Base.AdjustRotationTo(this.target, movingNode);
                this._dragNode.setScale(scale.x, scale.y);
                this._dragNode.setRotationX(rotation.x);
                this._dragNode.setRotationY(rotation.y);

                this.target.replaceParentSamePlace(this._dragNode);
            }

            cleverapps.focusManager.display({
                focus: "dragging",
                stack: true,
                control: this.dragControls,
                keepControls: this.dragControls === undefined,
                action: function (f) {
                    cleverapps.focusManager.setEventNodes([this.target]);
                    data.freeFocus = f;

                    data.changeFocusListener = cleverapps.focusManager.on("changeFocus", () => {
                        if (cleverapps.focusManager.focus !== "dragging") {
                            this.touches.delete(touch);
                            this.handleDragEnd(touch, event, data);
                        }
                    });
                }.bind(this)
            });
            return true;
        }
    }

    handleDragMove(touch, event) {
        this.onDragMove(touch, event);
    }

    handleDragEnd(touch, event, data) {
        if (this.followPointer) {
            if (this._originalTargetParent.isRunning()) {
                this.target.replaceParentSamePlace(this._originalTargetParent);
            } else {
                this.target.removeFromParent();
            }

            this._dragNode.removeFromParent();

            delete this._originalTargetParent;
            delete this._dragNode;
        }

        if (data.changeFocusListener) {
            data.changeFocusListener.clear();
            data.changeFocusListener = undefined;
        }
        if (data.freeFocus) {
            data.freeFocus();
            data.freeFocus = undefined;
        }

        this.onDragEnd(touch, event);
    }

    handleMouseScroll(event) {
        if (!this.needsToHandleMouseScroll) {
            return;
        }

        if (!this.isEnabled()) {
            return;
        }

        if (!this.hitTestTouch(event) || !this.filter(event)) {
            return;
        }

        this.processMouseScroll(event);

        return true;
    }

    processMouseMove(event) {
        if (!this.needsToHandleMouseMove) {
            return;
        }
        if (!this.isEnabled()) {
            return;
        }

        if (this.onMouseOver || this.onMouseOut || this.interactiveScale) {
            const wasHovered = this.isHovered;

            if (this.hitTestTouch(event) && this.filter()) {
                PointerHandler.setState(this, "hovered");

                this.isHovered = true;
            } else {
                PointerHandler.removeState(this, "hovered");

                this.isHovered = false;
            }

            if (wasHovered !== this.isHovered) {
                if (this.isHovered) {
                    if (this.onMouseOver) {
                        this.onMouseOver();
                    }

                    this._updateInteractiveScale();
                } else {
                    if (this.onMouseOut) {
                        this.onMouseOut();
                    }

                    this._updateInteractiveScale();
                }
            }
        }

        if (this.onMouseMove && cleverapps.focusManager.checkEventNode(this.target)) {
            this.onMouseMove(event);
        }
    }

    processMouseScroll(event) {
        const scrollY = event.getScrollY();
        const scrollX = event.getScrollX ? event.getScrollX() : 0;

        // If onMouseScroll is defined, call it with raw deltas (for scrolling content)
        if (this.onMouseScroll) {
            this.onMouseScroll(scrollY, scrollX);
            return;
        }

        // Otherwise, process for zoom (onMouseZoom)
        const abs = Math.abs(scrollY);
        const sign = Math.sign(scrollY);

        const macTouchpad = abs < 50;
        const k = macTouchpad ? 0.01 : 0.0015;

        let delta = scrollY * k;
        if (Math.abs(delta) > 0.1) {
            delta = sign * 0.1;
        }

        // cleverapps.config.debugMode && console.log("mouseScroll", delta, scrollY, Date.now() % 10000);

        // Detent-jitter filter for real mouse wheels: a single wheel click
        // sometimes produces a small opposite-direction spurious event.
        // Trackpads don't have detents, so skip the filter there — otherwise
        // a deliberate direction reversal eats ~64 ms of input (4 dropped
        // events at 16 ms cadence) and the zoom visibly hitches.
        if (!macTouchpad) {
            const now = Date.now();

            const prevSign = this.mouseScrollSign || 0;
            const prevTime = this.mouseScrollTime || 0;
            const bounces = this.mouseScrollBounces || 0;

            const dt = now - prevTime;
            const directionChanged = prevSign && sign !== prevSign;

            this.mouseScrollTime = now;

            if (directionChanged && dt < 150 && bounces + 1 <= 4) {
                this.mouseScrollBounces = bounces + 1;
                return;
            }

            this.mouseScrollSign = sign;
            this.mouseScrollBounces = 0;
        }

        this.onMouseZoom(delta);
    }

    calcPinchDistance(a, b) {
        return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    }
}

PointerHandler.TargetStates = new Map();

PointerHandler.STATE_NONE = 0;
PointerHandler.STATE_CLICK = 1;
PointerHandler.STATE_DRAG_START = 2;
PointerHandler.STATE_DRAG_MOVE = 3;
PointerHandler.STATE_LONG_PRESS = 4;

// Which gesture claimed a touch (touch.gesture) — inspection label only, see claimTouch.
PointerHandler.GESTURE_NONE = 0;
PointerHandler.GESTURE_CLICK = 1;
PointerHandler.GESTURE_DRAG = 2;
PointerHandler.GESTURE_PINCH = 3;
PointerHandler.GESTURE_PRESS = 4;
PointerHandler.GESTURE_LONG_PRESS = 5;
PointerHandler.GESTURE_SWALLOW = 6;

PointerHandler.DRAG_THROTTLE = 50;
PointerHandler.LONG_CLICK_TIMEOUT = 400;
PointerHandler.DOUBLE_CLICK_WAIT = 300;
PointerHandler.DRAG_HOLD_TIME = 300;

PointerHandler.blocked = false;

cleverapps.styles.PointerHandler = {
    clickThreshold: 20
};
